import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { Route, Switch, useHistory} from "react-router-dom";
import WithSidePanel from "root/components/WithSidePanel";
import RouteWithSubRoutes from "root/components/RouteWithSubRoutes";
import CategoriesPage from "../CategoriesPage";
import Header from "../Header";
import FiltersPanel from "../FiltersPanel";
import { useUI, useSearchParams, useDisabledFilterParams } from "../../getty-selectors";
import { SearchParams } from "../../data/types";
import { getInternalPath } from "../../utils";
import { AuthContainer, ContentBox } from "./AuthContent.styles";


export default ({ routes, applicationRoutes, source, filtersMap, actions, filtersByMediaType }) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { isFiltersPanelOpen, areFiltersSet } = useUI(source);
	const searchParams = useSearchParams(source);
	const disabledFilters = useDisabledFilterParams(source);
	const filterSet = filtersByMediaType[searchParams.mediaType];

	const handleSearch = useCallback(
		(params: SearchParams) => {
			const path = getInternalPath(source, applicationRoutes.SEARCH);
			if (history.location.pathname !== path) {
				history.push(path);
			}
			dispatch(actions.handleSearchAction(params));
		},
		[source, applicationRoutes.SEARCH, history, dispatch, actions]
	);
	const handleLogout = useCallback(() => {
		dispatch(actions.handleLogoutAction());
		history.push(getInternalPath(source, applicationRoutes.HOME));
	}, [actions, applicationRoutes.HOME, dispatch, history, source]);

	const handleClearSearchTerm = useCallback(
		(params: SearchParams) => {
			dispatch(actions.handleClearSearchTermAction());
			if (!areFiltersSet) {
				history.push(getInternalPath(source, applicationRoutes.HOME));
			}
			else {
				handleSearch({});
			}
		},
		[dispatch, actions, areFiltersSet, history, source, applicationRoutes.HOME, handleSearch]
	);
	const handleTogglePanel = useCallback(
		() => dispatch(actions.handleTogglePanelAction()),
		[actions, dispatch]
	);
	const clearSearchFilters = useCallback(
		() => {
			dispatch(actions.handleClearSearchParamsAction());
			handleSearch({});
		},
		[actions, dispatch, handleSearch]
	);
	const handleSelectAssets = useCallback(
		(items) => dispatch(actions.handleSelectAssetsAction({ items })),
		[actions, dispatch]
	);
	const handleRetrieveProduct = useCallback(
		() => dispatch(actions.retrieveProductAction()),
		[actions, dispatch]
	);

	const sidePanel = useCallback(
		() => (
			<FiltersPanel
				source={source}
				handleSearch={handleSearch}
				clearSearchFilters={clearSearchFilters}
				areFiltersSet={areFiltersSet}
				isFiltersPanelOpen={isFiltersPanelOpen}
				filtersMap={filtersMap}
				filterSet={filterSet}
			/>
		),
		[source, handleSearch, clearSearchFilters, areFiltersSet, isFiltersPanelOpen, filtersMap, filterSet]
	);

	return (
		<AuthContainer className="holder large-thin-menu-holder">
			<WithSidePanel sidePanel={sidePanel} openSidePanel={isFiltersPanelOpen}>
				<div className="tab-authed">
					<Header
						handleLogout={handleLogout}
						handleSearch={handleSearch}
						handleClearSearchTerm={handleClearSearchTerm}
						handleTogglePanel={handleTogglePanel}
						isFiltersPanelOpen={isFiltersPanelOpen}
						areFiltersSet={areFiltersSet}
						source={source}
						routes={applicationRoutes}
					/>
					<ContentBox>
						<Switch>
							<Route
								exact
								path={getInternalPath(source, applicationRoutes.HOME)}
								render={() => <CategoriesPage
									source={source}
									handleSearch={handleSearch}
									onSelect={handleSelectAssets}
									actions={actions}
								/>}
							/>
							<SubRoutes
								source={source}
								routes={routes}
								actions={actions}
								filterSet={filterSet}
								disabledFilters={disabledFilters}
								handleSearch={handleSearch}
								handleSelectAssets={handleSelectAssets}
								retrieveProduct={handleRetrieveProduct}
							/>
						</Switch>
					</ContentBox>
				</div>
			</WithSidePanel>
		</AuthContainer>
	);
};


export const SubRoutes = ({
	routes,
	handleSearch,
	handleSelectAssets,
	retrieveProduct,
	source,
	actions,
	filterSet,
	disabledFilters,
	isMultiple = false
}) => {
	return routes.map((r: {| path: string |}): Object => (
		<RouteWithSubRoutes
			key={`st_route_${r.path}`}
			{...r}
			handleSearch={handleSearch}
			onSelect={handleSelectAssets}
			retrieveProduct={retrieveProduct}
			actions={actions}
			source={source}
			filterSet={filterSet}
			disabledFilters={disabledFilters}
			isMultiple={isMultiple}
		/>
	));
};
